import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  Clock, Eye, ArrowRight, Zap,
  DollarSign, Shield, Crown, Palette, Brain, BarChart3
} from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const AVATAR_MAP = {
  hipo_thumbsup: '/hipo_thumbsup.webp', hipo_thinking: '/hipo_thinking.webp',
  hipo_checkmark: '/hipo_checkmark.webp', hipo_laptop: '/hipo_laptop.webp',
  hipo_sad: '/hipo_sad.webp', hipo_heart: '/hipo_heart.webp', hipo_sunglasses: '/hipo_sunglasses.webp',
};

const CATEGORY_COLORS = {
  Future: 'bg-blue-500/15 text-blue-400', Money: 'bg-green-500/15 text-green-400',
  System: 'bg-red-500/15 text-red-400', Power: 'bg-purple-500/15 text-purple-400',
  Style: 'bg-pink-500/15 text-pink-400', Mind: 'bg-amber-500/15 text-amber-400',
  'AI Insights': 'bg-cyan-500/15 text-cyan-400',
};

const AGENTS = [
  { avatar: 'hipo_laptop', name: 'Tech Analyst', role: 'Future & AI Insights', desc: 'Tracks EV trends, autonomous driving, and AI in automotive' },
  { avatar: 'hipo_thinking', name: 'Finance Guru', role: 'Money & Mind', desc: 'Leasing vs buying, cost of ownership, smart car decisions' },
  { avatar: 'hipo_checkmark', name: 'Safety Inspector', role: 'System', desc: 'VIN checks, scam alerts, dealer verification, buyer protection' },
  { avatar: 'hipo_sunglasses', name: 'Style Editor', role: 'Power & Style', desc: 'What successful people drive, car-lifestyle match, status' },
  { avatar: 'hipo_heart', name: 'Community Voice', role: 'Lifestyle', desc: 'Stories from real owners, road trips, car culture' },
  { avatar: 'hipo_thumbsup', name: 'Deal Hunter', role: 'AI Insights', desc: 'Market trends, best deals this week, price predictions' },
];

const MagazinePage = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const CATEGORIES = [
    { id: 'all', label: t('mag.allCategories'), icon: null },
    { id: 'Future', label: 'Future', icon: Zap },
    { id: 'Money', label: 'Money', icon: DollarSign },
    { id: 'System', label: 'System', icon: Shield },
    { id: 'Power', label: 'Power', icon: Crown },
    { id: 'Style', label: 'Style', icon: Palette },
    { id: 'Mind', label: 'Mind', icon: Brain },
    { id: 'AI Insights', label: 'AI Insights', icon: BarChart3 },
  ];

  useEffect(() => {
    setLoading(true);
    const params = activeCategory === 'all' ? { limit: 30 } : { category: activeCategory, limit: 30 };
    axios.get(`${API}/magazine/articles`, { params })
      .then(r => setArticles(r.data.filter(a => (a.status || 'published') === 'published')))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const hero = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <div className="flex items-center gap-3 mb-2">
          <img src="/hipo_sunglasses.webp" alt="Hipo" className="w-10 h-10 sm:w-14 sm:h-14" />
          <div>
            <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold uppercase">
              {t('mag.title1')} <span className="text-cyan-500 neon-text">{t('mag.title2')}</span>
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm">{t('mag.tagline')}</p>
          </div>
        </div>
      </div>

      {/* Agents Strip */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-gray-600 mb-3">{t('mag.aiWriters')}</p>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
          {AGENTS.map((agent, i) => (
            <div key={i} className="shrink-0 w-36 sm:w-44 rounded-xl p-3 bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-colors">
              <img src={AVATAR_MAP[agent.avatar]} alt={agent.name} className="w-10 h-10 mb-2" />
              <p className="text-xs font-semibold text-white">{agent.name}</p>
              <p className="text-[9px] text-cyan-500 mb-1">{agent.role}</p>
              <p className="text-[9px] text-gray-600 line-clamp-2">{agent.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeCategory === cat.id ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20' : 'text-gray-500 hover:text-white bg-white/[0.03] border border-transparent'}`} data-testid={`mag-cat-${cat.id}`}>
              {cat.icon && <cat.icon className="w-3.5 h-3.5" />}{cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className="rounded-2xl bg-white/5 h-60 animate-pulse" />)}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white/[0.02] border border-white/5">
            <img src="/hipo_sad.webp" alt="" className="w-16 h-16 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">{t('mag.noArticles')}</h2>
            <p className="text-gray-500 text-sm">{t('mag.noArticlesDesc')}</p>
          </div>
        ) : (
          <>
            {/* Hero Article */}
            {hero && (
              <Link to={`/magazine/${hero.slug}`} className="group block rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden hover:border-cyan-500/20 transition-all mb-6" data-testid={`article-${hero.slug}`}>
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 p-6 flex flex-col justify-center bg-gradient-to-br from-cyan-500/5 to-transparent">
                    <img src={AVATAR_MAP[hero.avatar] || AVATAR_MAP.hipo_thumbsup} alt="" className="w-16 h-16 mb-4" />
                    <span className={`self-start px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider mb-2 ${CATEGORY_COLORS[hero.category] || ''}`}>{hero.category}</span>
                    <div className="flex items-center gap-2 text-[10px] text-gray-600"><Clock className="w-3 h-3" />{hero.read_time_minutes} {t('mag.minRead')}</div>
                  </div>
                  <div className="md:w-2/3 p-6 flex flex-col justify-center">
                    <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold group-hover:text-cyan-400 transition-colors leading-tight mb-3">{hero.title}</h2>
                    <p className="text-gray-500 text-sm line-clamp-3 mb-4">{hero.meta_description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{t('mag.hipoWriter')}</span>
                        <span className="text-[10px] text-gray-600">{new Date(hero.published_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-cyan-500 group-hover:gap-2 transition-all">{t('mag.read')} <ArrowRight className="w-3.5 h-3.5" /></div>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {rest.map(article => (
                  <ArticleCard key={article.id} article={article} t={t} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ArticleCard = ({ article, t }) => {
  const avatar = AVATAR_MAP[article.avatar] || AVATAR_MAP.hipo_thumbsup;
  const catColor = CATEGORY_COLORS[article.category] || '';

  return (
    <Link to={`/magazine/${article.slug}`} className="group block rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden hover:border-cyan-500/20 transition-all" data-testid={`article-${article.slug}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${catColor}`}>{article.category}</span>
          <span className="text-[10px] text-gray-600 flex items-center gap-1"><Clock className="w-3 h-3" />{article.read_time_minutes}m</span>
        </div>
        <h3 className="font-heading text-base font-bold group-hover:text-cyan-400 transition-colors leading-tight mb-2 line-clamp-2">{article.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 mb-4">{article.meta_description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <div className="flex items-center gap-2">
            <img src={avatar} alt="" className="w-6 h-6" />
            <span className="text-[10px] text-gray-400">{new Date(article.published_at).toLocaleDateString()}</span>
          </div>
          <span className="flex items-center gap-1 text-[10px] text-gray-600"><Eye className="w-3 h-3" />{article.views}</span>
        </div>
      </div>
    </Link>
  );
};

export default MagazinePage;
